import {
    FC,
    ReactElement,
    useEffect,
    useMemo,
    useState,
} from 'react';

import { useNavigate, useParams } from 'react-router-dom';
import { useDebounce } from 'react-use';

import { OrganisationDetailOccupation } from '../../../containers';
import { OrganisationDetailRouteTab } from '../../../containers/@organisation/OrganisationDetailTabs/OrganisationDetailTabs';
import { transformOccupationToSearchableOption } from '../../../models/@competentNL/Occupations/OccupationsTransformers';
import { SkillBase, SkillsFormData } from '../../../models/@competentNL/Skills/Skills';
import { CompetencyFormData } from '../../../models/Competencies/Competencies';
import { OrganisationOccupation, OrganisationOccupationFormData } from '../../../models/OrganisationOccupation/OrganisationOccupation';
import { fetchCompetencies } from '../../../redux/competencies/competenciesActions';
import { setOccupation } from '../../../redux/competentNL/competentNL';
import {
    fetchCompetentNLOccupation,
    fetchCompetentNLOccupations,
    fetchCompetentNLSkills,
} from '../../../redux/competentNL/competentNLActions';
import { setIsDeleted } from '../../../redux/organisationOccupation/organisationOccupation';
import { deleteOrganisationOccupation, fetchOrganisationOccupation, updateOrganisationOccupation } from '../../../redux/organisationOccupation/organisationOccupationActions';
import { useTypedDispatch, useTypedSelector } from '../../../redux/store';
import { RoutePaths } from '../../../Routes';
import { SearchableOption } from '../../../types';

interface ConnectedOrganisationDetailOccupationProps {
    className?: string;
}

const ConnectedOrganisationDetailOccupation: FC<ConnectedOrganisationDetailOccupationProps> = ({
    className = '',
}): ReactElement => {
    const dispatch = useTypedDispatch();
    const navigate = useNavigate();
    const { occupationUuid } = useParams();

    const occupationIsLoading = useTypedSelector(state => state.organisationOccupationReducer.isLoading);
    const occupationIsUpdating = useTypedSelector(state => state.organisationOccupationReducer.isUpdating);
    const occupationIsSuccessful = useTypedSelector(state => state.organisationOccupationReducer.isSuccessful);
    const occupationIsDeleted = useTypedSelector(state => state.organisationOccupationReducer.isDeleted);
    const pageError = useTypedSelector(state => state.organisationOccupationReducer.error);
    const formError = useTypedSelector(state => state.organisationOccupationReducer.formError);
    const organisationOccupation = useTypedSelector(state => state.organisationOccupationReducer.occupation);

    const organisationIsLoading = useTypedSelector(state => state.organisationReducer.isLoading);
    const organisation = useTypedSelector(state => state.organisationReducer.organisation);

    const occupations = useTypedSelector(state => state.competentNLReducer.occupations);
    const occupation = useTypedSelector(state => state.competentNLReducer.occupation);
    const skillsIsLoading = useTypedSelector(state => state.competentNLReducer.skillsIsLoading);
    const skillBases = useTypedSelector(state => state.competentNLReducer.skillBases);
    const skillsError = useTypedSelector(state => state.competentNLReducer.skillsError);

    const competencyOptions = useTypedSelector(state => state.competenciesReducer.competencies);

    const [skillQuery, setSkillQuery] = useState<string>('');
    const [occupationQuery, setOccupationQuery] = useState<string>('');

    const occupationOptions = useMemo((): SearchableOption[] => (
        occupations.map(transformOccupationToSearchableOption)
    ), [occupations]);

    const occupationSkillOptions = useMemo((): SkillBase[] => {
        if (!occupation) {
            return [];
        }

        const { essentialSkills, importantSkills, extraSkills } = occupation;

        return [...essentialSkills, ...importantSkills, ...extraSkills];
    }, [occupation]);

    useEffect((): void => {
        if (competencyOptions.length === 0) {
            dispatch(fetchCompetencies());
        }
    }, []);

    useEffect((): () => void => {
        if (occupationIsDeleted && organisation) {
            navigate(RoutePaths.organisationDetailTab(organisation.id, OrganisationDetailRouteTab.occupationOverview));
        }

        return (): void => {
            dispatch(setIsDeleted(false));
        };
    }, [occupationIsDeleted]);

    useEffect((): void => {
        if (occupationUuid && organisation) {
            dispatch(fetchOrganisationOccupation(organisation?.id, occupationUuid));
        }
    }, [organisation, occupationUuid]);

    useDebounce((): void => {
        if (skillQuery.length > 1) {
            dispatch(fetchCompetentNLSkills(skillQuery));
        }
    }, 500, [skillQuery]);

    useDebounce((): void => {
        if (occupationQuery) {
            dispatch(fetchCompetentNLOccupations(occupationQuery));
            return;
        }

        dispatch(setOccupation(undefined));
    }, 250, [occupationQuery]);

    const handleSelectOccupation = (occupationId: string): void => {
        dispatch(fetchCompetentNLOccupation(occupationId));
    };

    const handleUpdateSkills = (skillsFormData: SkillsFormData): void => {
        if (organisation) {
            const formData: OrganisationOccupationFormData = {
                ...organisationOccupation,
                escoSkills: organisationOccupation.escoSkills.map(escoSkill => escoSkill.id),
                skills: skillsFormData.skills.map(skill => skill.id),
                competencies: organisationOccupation.competencies.map(competency => competency.id),
            };

            dispatch(updateOrganisationOccupation(formData, organisation.id));
        }
    };

    const handleCloseSkillsDialog = (): void => {
        dispatch(setOccupation(undefined));
    };

    const handleUpdateCompetencies = (competenciesFormData: CompetencyFormData): void => {
        if (organisation) {
            const formData: OrganisationOccupationFormData = {
                ...organisationOccupation,
                escoSkills: organisationOccupation.escoSkills.map(skill => skill.id),
                skills: organisationOccupation.skills.map(skill => skill.id),
                competencies: competenciesFormData.competencies,
            };

            dispatch(updateOrganisationOccupation(formData, organisation.id));
        }
    };

    const handleOccupationDeleteSubmit = (occupationToDelete: OrganisationOccupation): void => {
        if (organisation) {
            dispatch(deleteOrganisationOccupation(occupationToDelete, organisation.id));
        }
    };

    const handleOccupationFormSubmit = (formData: OrganisationOccupationFormData): void => {
        if (organisation) {
            dispatch(updateOrganisationOccupation(formData, organisation.id));
        }
    };

    return (
        <OrganisationDetailOccupation
            isLoading={organisationIsLoading || occupationIsLoading}
            suggestionIsLoading={skillsIsLoading}
            isUpdating={occupationIsUpdating}
            isSuccessful={occupationIsSuccessful}
            pageError={pageError}
            formError={formError}
            occupation={organisationOccupation}
            organisationUuid={organisation?.id}
            searchedSkills={skillBases}
            suggestedSkills={occupationSkillOptions}
            skillsError={skillsError}
            occupationOptions={occupationOptions}
            competencyOptions={competencyOptions}
            onOccupationFormSubmit={handleOccupationFormSubmit}
            onOccupationDeleteSubmit={handleOccupationDeleteSubmit}
            onSkillSearchChange={setSkillQuery}
            onOccupationSearchChange={setOccupationQuery}
            onOccupationSearchSelect={handleSelectOccupation}
            onUpdateSkills={handleUpdateSkills}
            onCloseSkillsDialog={handleCloseSkillsDialog}
            onUpdateCompetencies={handleUpdateCompetencies}
            className={className}
        />
    );
};

export default ConnectedOrganisationDetailOccupation;
